import type {ReactNode} from 'react';
import type {Tone} from '@/components/app/feedback';
import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {identitiesApi} from '../../app/services/identitiesApi';
import {logout} from '../auth/authSlice';
import {AssistantContent} from './types';

export interface PageMessage {
  id?: string;
  content?: ReactNode;
  severity?: Tone;
  dismissable?: boolean;
}

export interface CoreState {
  assistantContents: AssistantContent[];
  blockUICount: number;
  pageMessages: PageMessage[];
}

export const initialState: CoreState = {
  assistantContents: [],
  blockUICount: 0,
  pageMessages: [],
};

export const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    hidePageMessage: (state, action: PayloadAction<{id: string}>) => {
      const index = state.pageMessages.findIndex((pageMessage) => pageMessage.id === action.payload.id);
      if (index !== -1) {
        state.pageMessages.splice(index, 1);
      }
    },
    showPageMessage: (state, action: PayloadAction<PageMessage>) => {
      state.pageMessages.push(action.payload);
    },
    popAssistantContent: (state) => {
      state.assistantContents.pop();
    },
    pushAssistantContent: (state, action: PayloadAction<AssistantContent>) => {
      state.assistantContents = state.assistantContents.map((assistantContent) => ({
        ...assistantContent,
        detailDelay: 0,
        detailInterval: 0,
        summaryDelay: 0,
        summaryInterval: 0,
      }));
      state.assistantContents.push(action.payload);
    },
    removeAssistantContent: (state, action: PayloadAction<string>) => {
      state.assistantContents = state.assistantContents.filter(
        (assistantContent) => assistantContent.id !== action.payload,
      );
    },
    setAssistantContent: (state, action: PayloadAction<AssistantContent | null>) => {
      state.assistantContents = action.payload ? [action.payload] : [];
    },
    blockUI: (state) => {
      state.blockUICount++;
    },
    unblockUI: (state) => {
      state.blockUICount--;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => {
      return initialState;
    });
    builder.addMatcher(identitiesApi.endpoints.registerVerifyOTP.matchFulfilled, () => {
      return initialState;
    });
  },
});

export const {
  hidePageMessage,
  showPageMessage,
  removeAssistantContent,
  popAssistantContent,
  pushAssistantContent,
  setAssistantContent,
  blockUI,
  unblockUI,
} = coreSlice.actions;

export const selectAssistantContents = (state: {core: CoreState}) => state.core.assistantContents;

export const selectPageMessages = (state: {core: CoreState}) => state.core.pageMessages;

export const selectUIBlocked = (state: {core: CoreState}) => state.core.blockUICount > 0;

export default coreSlice.reducer;
